import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Input } from "../../base/input/input";
import { Switch } from "../../base/switch/switch";
import { DataTableViewOptions } from "./DataTableViewOptions";
/**
 * A unified toolbar component for DataTable that consolidates common actions:
 * - Global search
 * - Quick filters (toolbar-level filters for common fields)
 * - Filter chips (URL-based column filters)
 * - Active filter chips (TanStack Table filters)
 * - Column visibility toggle
 * - Custom toolbar content
 *
 * UX Pattern:
 * - Primary actions (search, key filters) in toolbar
 * - Filter chips show active URL-based filters
 * - Per-column filters still available via column headers for power users
 * - Hybrid approach provides flexibility for simple and complex use cases
 *
 * @example
 * ```tsx
 * <DataTableToolbar
 *   table={table}
 *   enableGlobalFilter
 *   globalFilterValue={globalFilter}
 *   onGlobalFilterChange={setGlobalFilter}
 *   globalSearchPlaceholder="Search users..."
 *   filterChips={<>...filter chip components...</>}
 *   quickFilters={[
 *     {
 *       id: 'status',
 *       label: 'Status',
 *       render: () => <StatusSelect />
 *     }
 *   ]}
 * />
 * ```
 */ export function DataTableToolbar({ table, enableGlobalFilter = false, globalFilterValue = "", onGlobalFilterChange, globalSearchPlaceholder, enableColumnVisibility = false, quickFilters = [], groupingToggle, children, filterChips }) {
    const { t } = useTranslation();
    const hasFilterChips = !!filterChips;
    const groupByLabel = groupingToggle ? t("dataTable.groupBy", {
        defaultValue: "Group by {{label}}",
        label: groupingToggle.label
    }) : "";
    // Don't render toolbar if no features are enabled
    if (!enableGlobalFilter && !enableColumnVisibility && !quickFilters.length && !groupingToggle && !children && !hasFilterChips) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-1 items-center gap-2",
                        children: [
                            enableGlobalFilter && onGlobalFilterChange && /*#__PURE__*/ _jsxs("div", {
                                className: "relative max-w-sm flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Search, {
                                        className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        placeholder: globalSearchPlaceholder || t("dataTable.search", {
                                            defaultValue: "Search..."
                                        }),
                                        value: globalFilterValue,
                                        onChange: (e)=>onGlobalFilterChange(e.target.value),
                                        className: "pl-9"
                                    })
                                ]
                            }),
                            quickFilters.map((filter)=>/*#__PURE__*/ _jsx("div", {
                                    children: filter.render()
                                }, filter.id)),
                            children
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            groupingToggle && /*#__PURE__*/ _jsxs("label", {
                                className: "flex items-center gap-2 text-sm text-muted-foreground",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        children: groupByLabel
                                    }),
                                    /*#__PURE__*/ _jsx(Switch, {
                                        checked: groupingToggle.isGrouped,
                                        onCheckedChange: groupingToggle.onToggle,
                                        "aria-label": groupByLabel
                                    })
                                ]
                            }),
                            enableColumnVisibility && /*#__PURE__*/ _jsx(DataTableViewOptions, {
                                table: table
                            })
                        ]
                    })
                ]
            }),
            hasFilterChips && /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap items-center gap-2",
                children: filterChips
            })
        ]
    });
}

//# sourceMappingURL=DataTableToolbar.js.map